import { createCollection } from '@rocketlane/state-db';
import { getPhaseFromCollection } from 'collections/phase';
import { taskCollection } from 'collections/tasks';
import { converToEntityId, extractParts, PhaseEntityType } from 'data/projects/phase-entity';
/** Stable serialization of the plan filters for lane identity. */ export const getPlanFiltersHash = (filters)=>{
    var _ref, _ref1, _ref2, _ref3;
    return JSON.stringify([
        (_ref = filters === null || filters === void 0 ? void 0 : filters.completed) !== null && _ref !== void 0 ? _ref : null,
        (_ref1 = filters === null || filters === void 0 ? void 0 : filters.responsibleCompanyIds) !== null && _ref1 !== void 0 ? _ref1 : [],
        (_ref2 = filters === null || filters === void 0 ? void 0 : filters.includePrivate) !== null && _ref2 !== void 0 ? _ref2 : true,
        (_ref3 = filters === null || filters === void 0 ? void 0 : filters.budgetIds) !== null && _ref3 !== void 0 ? _ref3 : []
    ]);
};
export const getPlanLaneKey = (projectId, phaseId, filters)=>`${projectId}:${phaseId}:${getPlanFiltersHash(filters)}`;
/**
 * Same key, from an already-hashed lane rather than raw filters. Used when
 * moving between two lanes that must share a filter set — the hash is the only
 * form the source lane still has.
 */ export const getPlanLaneKeyFromHash = (projectId, phaseId, filtersHash)=>`${projectId}:${phaseId}:${filtersHash}`;
export const planLaneCollection = createCollection({
    id: 'plan-lanes',
    getKey: (lane)=>lane.laneKey,
    indexes: {
        byProject: (lane)=>lane.projectId,
        byPhase: (lane)=>lane.projectPhaseId
    }
});
export const planBoardPhaseCollection = createCollection({
    id: 'plan-board-phases',
    getKey: (entry)=>entry.projectPhaseId,
    indexes: {
        byProject: (entry)=>entry.projectId
    }
});
/**
 * Rewrite every loaded lane of a phase to a new in-lane token order.
 *
 * Applies an ORDERING INTENT, not a window: `newOrder` is matched against
 * whatever tokens each lane actually holds, and tokens outside it keep their
 * relative position at the end. That is what makes the same call correct on a
 * peer tab holding a different window — three pages deep or one — and is why the
 * cross-tab signal carries this list rather than a copy of `pageTokens`.
 *
 * Deliberately does NOT broadcast. `reorderPlanEntityTokensOptimistic` is the
 * broadcasting wrapper; the cross-tab receiver calls THIS. Splitting the two
 * makes a re-broadcast loop structurally impossible instead of guarded against.
 */ export const applyPlanEntityReorderTokens = (projectId, phaseId, newOrder)=>{
    const lanes = planLaneCollection.getByIndex('byPhase', phaseId).filter((lane)=>lane.projectId === projectId);
    if (lanes.length === 0) {
        return;
    }
    const orderIndex = new Map(newOrder.map((token, index)=>[
            token,
            index
        ]));
    planLaneCollection.push(lanes.map((lane)=>{
        const inWindow = lane.pageTokens.filter((token)=>orderIndex.has(token));
        const outsideWindow = lane.pageTokens.filter((token)=>!orderIndex.has(token));
        inWindow.sort((left, right)=>{
            var _orderIndex_get, _orderIndex_get1;
            return ((_orderIndex_get = orderIndex.get(left)) !== null && _orderIndex_get !== void 0 ? _orderIndex_get : 0) - ((_orderIndex_get1 = orderIndex.get(right)) !== null && _orderIndex_get1 !== void 0 ? _orderIndex_get1 : 0);
        });
        return {
            ...lane,
            pageTokens: [
                ...inWindow,
                ...outsideWindow
            ]
        };
    }), // `'local'`: the peer applies the ORDER SIGNAL and derives its own tokens
    // from it. Broadcasting the resulting window would hand peers a slice of
    // this tab's pagination state, which is exactly what the signal avoids.
    {
        source: 'local'
    });
};
/** Resolve an anchor payload to an insertion index within `tokens`. */ const resolveInsertAt = (tokens, payload)=>{
    const afterIndex = payload.afterEntityId ? tokens.indexOf(payload.afterEntityId) : -1;
    const beforeIndex = payload.beforeEntityId ? tokens.indexOf(payload.beforeEntityId) : -1;
    if (afterIndex !== -1) {
        return afterIndex + 1;
    }
    if (beforeIndex !== -1) {
        return beforeIndex;
    }
    return payload.anchor === 'TOP' ? 0 : tokens.length;
};
/**
 * Move one entity's token between phases: out of every source lane, into every
 * destination lane at the anchor-resolved slot, with the task's own
 * `projectPhase` and the board header counts brought along.
 *
 * Everything here is derived from `payload` alone, so it produces the right
 * result on ANY tab regardless of how deep its lanes are paginated — the same
 * property that lets the in-lane reorder signal carry an intent rather than a
 * window (see `applyPlanEntityReorderTokens`). That is what makes a cross-phase
 * DnD reflect in a peer tab with no fetch at all.
 *
 * Every write is `source: 'local'`, which is both correct and load-bearing:
 * correct because until the move persists this is a guess, and load-bearing
 * because it means running this on a receiving tab emits nothing back onto the
 * channel. No loop is possible.
 *
 * Returns `applied: false` when the move could not be represented here — see the
 * guard below — so a receiving tab can fall back to reading the lane instead of
 * showing a card with nothing behind it. `undo` reverts the task's phase patch,
 * the one piece no lane refetch restores.
 */ export const applyPlanEntityMoveTokens = (projectId, destPhaseId, payload, { requireEntity = false, sprint } = {})=>{
    const { entityId, fromPhaseId } = payload;
    const parts = extractParts(entityId);
    let undoEntityPhase = ()=>undefined;
    // Opt-in guard, for the CROSS-TAB path only.
    //
    // A task token renders by joining against `taskCollection`, so applying a move
    // for a task this tab never loaded inserts a token with no entity behind it —
    // an invisible card, with the counts bumped for it. The lane ingest is what
    // fetches that entity, so a peer holding the DESTINATION lane but not the task
    // must go and read the lane rather than take the move on faith.
    //
    // The originating tab is the opposite case: it is dragging a card that it may
    // legitimately hold only as a lane token (a deep window whose entity page is
    // not loaded), and its optimistic insert should still happen. Hence off by
    // default — the caller that cannot fetch is the one that has to ask for it.
    //
    // Sprints are exempt either way: they live on the lane record itself and have
    // no collection entry that could be missing.
    if (requireEntity && parts.entityType === PhaseEntityType.Task && taskCollection.get(parts.id) === undefined) {
        return {
            applied: false,
            undo: undoEntityPhase
        };
    }
    // Cards, filters and the task drawer read `projectPhase` off the collection
    // task; without this the moved card renders in the destination lane while its
    // data still claims the source phase.
    if (parts.entityType === PhaseEntityType.Task) {
        const task = taskCollection.get(parts.id);
        const destPhase = getPhaseFromCollection(destPhaseId);
        if (task) {
            var _task_projectPhase;
            taskCollection.push([
                {
                    ...task,
                    projectPhase: {
                        ...(_task_projectPhase = task.projectPhase) !== null && _task_projectPhase !== void 0 ? _task_projectPhase : {},
                        projectPhaseId: destPhaseId,
                        ...(destPhase === null || destPhase === void 0 ? void 0 : destPhase.projectPhaseName) != null ? {
                            projectPhaseName: destPhase.projectPhaseName
                        } : {}
                    }
                }
            ], {
                source: 'local'
            });
            // Restores the exact pre-move entity. Without it a rejected move leaves
            // the task claiming a phase it never reached: the lane refetch puts the
            // token back in the source lane, the reconciler reads the stale
            // `projectPhase`, and strips it straight back out.
            undoEntityPhase = ()=>{
                taskCollection.push([
                    task
                ], {
                    source: 'local'
                });
            };
        }
    }
    const sourceLanes = planLaneCollection.getByIndex('byPhase', fromPhaseId).filter((lane)=>lane.projectId === projectId);
    const destLanes = planLaneCollection.getByIndex('byPhase', destPhaseId).filter((lane)=>lane.projectId === projectId);
    // A re-applied move (drop, then conflict-confirm) must not double-shift counts.
    const leftSource = sourceLanes.length === 0 || sourceLanes.some((lane)=>lane.pageTokens.includes(entityId));
    const enteredDest = destLanes.length === 0 || destLanes.some((lane)=>!lane.pageTokens.includes(entityId));
    // A sprint lives only in `PlanLane.sprints` — it must travel with its token,
    // or the destination renders a token with nothing behind it. Falls back to the
    // source lane's copy so a caller without the object (cross-tab) still works.
    const movedSprint = parts.entityType === PhaseEntityType.Sprint ? sprint !== null && sprint !== void 0 ? sprint : sourceLanes.flatMap((lane)=>{
        var _lane_sprints;
        return (_lane_sprints = lane.sprints) !== null && _lane_sprints !== void 0 ? _lane_sprints : [];
    }).find((laneSprint)=>laneSprint.sprintId === parts.id) : undefined;
    const sourcePatches = sourceLanes.map((lane)=>{
        var _lane_sprints, _lane_sprints1;
        const hasToken = lane.pageTokens.includes(entityId);
        const hasSprint = movedSprint != null && ((_lane_sprints = lane.sprints) !== null && _lane_sprints !== void 0 ? _lane_sprints : []).some((laneSprint)=>laneSprint.sprintId === movedSprint.sprintId);
        if (!hasToken && !hasSprint) {
            return undefined;
        }
        return {
            ...lane,
            ...hasToken ? {
                pageTokens: lane.pageTokens.filter((token)=>token !== entityId),
                totalCount: Math.max(0, lane.totalCount - 1),
                filteredCount: Math.max(0, lane.filteredCount - 1)
            } : {},
            ...hasSprint ? {
                sprints: ((_lane_sprints1 = lane.sprints) !== null && _lane_sprints1 !== void 0 ? _lane_sprints1 : []).filter((laneSprint)=>laneSprint.sprintId !== movedSprint.sprintId)
            } : {}
        };
    }).filter((lane)=>lane != null);
    if (sourcePatches.length > 0) {
        planLaneCollection.push(sourcePatches, {
            source: 'local'
        });
    }
    if (destLanes.length > 0) {
        planLaneCollection.push(destLanes.map((lane)=>{
            var _lane_sprints;
            // Dedup-safe: strip first, then insert, so re-applying resolves to the
            // same final state rather than double-inserting.
            const alreadyPresent = lane.pageTokens.includes(entityId);
            const tokens = lane.pageTokens.filter((token)=>token !== entityId);
            tokens.splice(resolveInsertAt(tokens, payload), 0, entityId);
            return {
                ...lane,
                pageTokens: tokens,
                totalCount: alreadyPresent ? lane.totalCount : lane.totalCount + 1,
                filteredCount: alreadyPresent ? lane.filteredCount : lane.filteredCount + 1,
                ...movedSprint ? {
                    sprints: [
                        ...((_lane_sprints = lane.sprints) !== null && _lane_sprints !== void 0 ? _lane_sprints : []).filter((laneSprint)=>laneSprint.sprintId !== movedSprint.sprintId),
                        movedSprint
                    ]
                } : {}
            };
        }), {
            source: 'local'
        });
    }
    // Board header counts (ghost sizing) — best-effort ±1; a verification
    // mismatch on settle refetches the board as the truth.
    const boardPatches = [
        {
            phaseId: fromPhaseId,
            delta: leftSource ? -1 : 0
        },
        {
            phaseId: destPhaseId,
            delta: enteredDest ? 1 : 0
        }
    ].map(({ phaseId, delta })=>{
        const entry = planBoardPhaseCollection.get(phaseId);
        if (!entry || entry.projectId !== projectId || delta === 0) {
            return undefined;
        }
        return {
            ...entry,
            entityCount: Math.max(0, entry.entityCount + delta)
        };
    }).filter((entry)=>entry != null);
    if (boardPatches.length > 0) {
        planBoardPhaseCollection.push(boardPatches, {
            source: 'local'
        });
    }
    return {
        applied: true,
        undo: undoEntityPhase
    };
};
/**
 * Strip tokens for tasks that no longer exist (delete, WS externalDelete) —
 * one central fixup instead of per-call-site cleanup. Extra safety net only:
 * rendering already skips tokens with no matching entity, and membership truth
 * always comes back via lane refetch.
 */ const reconcileDeadTokens = (changedIds)=>{
    const deadTokens = new Set();
    changedIds.forEach((id)=>{
        if (taskCollection.get(id) === undefined && typeof id === 'number') {
            deadTokens.add(converToEntityId(id, PhaseEntityType.Task));
        }
    });
    if (deadTokens.size === 0) {
        return;
    }
    const lanesToPatch = planLaneCollection.getAll().filter((lane)=>lane.pageTokens.some((token)=>deadTokens.has(token)));
    if (lanesToPatch.length === 0) {
        return;
    }
    planLaneCollection.push(lanesToPatch.map((lane)=>({
            ...lane,
            pageTokens: lane.pageTokens.filter((token)=>!deadTokens.has(token))
        })), {
        source: 'local'
    });
};
/**
 * Re-file a task's token when its phase changes.
 *
 * The board renders each phase from its lane's `pageTokens`, and until this
 * existed nothing moved a token between lanes. Dragging a card worked because
 * `movePlanEntityTokensOptimistic` patched the lanes by hand; moving the same
 * task from the task drawer did not, and the only remaining mechanism was a
 * lane refetch — `fetchLaneFirstPage`, which reads page ONE. A task that lands
 * past the first page of its destination is invisible until the lane is
 * scrolled, so the card simply stayed in the phase it left.
 *
 * Deriving it from the task entity instead fixes both tabs with one mechanism.
 * The tab that made the move sees it the instant the optimistic overlay
 * applies; a peer sees it when the entity arrives over the cross-tab channel.
 * Neither waits on a request, and there is no page boundary to fall past.
 *
 * Written with `source: 'local'`: every tab derives this from the same synced
 * task, so broadcasting the result would have peers apply it twice — once from
 * their own reconciler and once from the message. Same reason the phase
 * progress counters stay local.
 */ const reconcilePhaseTokens = (changedIds)=>{
    if (planLaneCollection.size() === 0) {
        return;
    }
    // Accumulated so several tasks moving at once (a bulk edit) fold into one
    // write per lane rather than one per task.
    const patched = new Map();
    const laneOf = (key)=>{
        var _patched_get;
        return (_patched_get = patched.get(key)) !== null && _patched_get !== void 0 ? _patched_get : planLaneCollection.get(key);
    };
    changedIds.forEach((id)=>{
        var _task_projectPhase, _task_project;
        if (typeof id !== 'number') {
            return;
        }
        const task = taskCollection.get(id);
        const phaseId = task === null || task === void 0 ? void 0 : (_task_projectPhase = task.projectPhase) === null || _task_projectPhase === void 0 ? void 0 : _task_projectPhase.projectPhaseId;
        const projectId = task === null || task === void 0 ? void 0 : (_task_project = task.project) === null || _task_project === void 0 ? void 0 : _task_project.projectId;
        if (phaseId == null || projectId == null) {
            return;
        }
        const token = converToEntityId(id, PhaseEntityType.Task);
        planLaneCollection.getByIndex('byProject', projectId).forEach(({ laneKey, projectPhaseId })=>{
            const stale = laneOf(laneKey);
            if (stale === undefined || projectPhaseId === phaseId || !stale.pageTokens.includes(token)) {
                return;
            }
            patched.set(laneKey, {
                ...stale,
                pageTokens: stale.pageTokens.filter((t)=>t !== token),
                totalCount: Math.max(0, stale.totalCount - 1),
                filteredCount: Math.max(0, stale.filteredCount - 1)
            });
            // The destination lane under the SAME filters. Matching the hash
            // matters: a task visible under one filter set in its old phase
            // belongs in that same filter set's lane for its new phase, and in no
            // other. A lane this tab has not loaded is simply skipped — there is
            // no window for the token to be part of.
            const destKey = getPlanLaneKeyFromHash(projectId, phaseId, stale.filtersHash);
            const dest = laneOf(destKey);
            if (dest === undefined || dest.pageTokens.includes(token)) {
                return;
            }
            // Appended, not positioned: a drawer move carries no anchor. On a
            // fully-loaded lane that is exactly where the server puts it; on a
            // partial window it is approximate, and the lane's next fetch settles
            // the real order.
            patched.set(destKey, {
                ...dest,
                pageTokens: [
                    ...dest.pageTokens,
                    token
                ],
                totalCount: dest.totalCount + 1,
                filteredCount: dest.filteredCount + 1
            });
        });
    });
    if (patched.size > 0) {
        planLaneCollection.push([
            ...patched.values()
        ], {
            source: 'local'
        });
    }
};
let reconcilerAttached = false;
/**
 * Keep lane token windows coherent with `taskCollection`, for the two changes
 * that invalidate them: a task disappearing, and a task changing phase.
 */ export const attachPlanLaneReconciler = ()=>{
    if (reconcilerAttached) {
        return;
    }
    reconcilerAttached = true;
    taskCollection.subscribeToChanges((changedIds)=>{
        reconcileDeadTokens(changedIds);
        reconcilePhaseTokens(changedIds);
    });
};
